/**
 * FE error mapping for typed slevový kód apply/remove (plan §3.17 / §11 / §20).
 * Invalid/exhausted/disabled/expired collapse to one user-facing string class.
 * Gate codes MODULE / TENANT / SHADOW stay distinct. Never map PROMO_DISABLED.
 */

import type { PromoCodeErrorCode } from 'pi-kiosk-shared/promo/PromoCodeErrorCode';
import { isPromoCodeErrorCode } from 'pi-kiosk-shared/promo/PromoCodeErrorCode';

export const PROMO_CODE_OPAQUE_FAILURE_CODES = [
  'PROMO_CODE_INVALID',
  'PROMO_CODE_EXHAUSTED',
  'PROMO_CODE_DISABLED',
  'PROMO_CODE_EXPIRED',
] as const satisfies readonly PromoCodeErrorCode[];

export type PromoCodeOpaqueFailureCode = (typeof PROMO_CODE_OPAQUE_FAILURE_CODES)[number];

export type PromoCodeApplyUserMessageKey =
  | 'promo.codeEntry.invalidClass'
  | 'promo.codeEntry.moduleDisabled'
  | 'promo.codeEntry.tenantDisabled'
  | 'promo.codeEntry.shadowOrDisabled'
  | 'promo.codeEntry.entitlementReadDenied'
  | 'promo.codeEntry.entitlementWriteDenied'
  | 'promo.codeEntry.salesPointNotAllowed'
  | 'promo.codeEntry.checkoutSessionRequired'
  | 'promo.codeEntry.guestSignInRequired'
  | 'promo.codeEntry.channelDenied'
  | 'promo.codeEntry.minSpend'
  | 'promo.codeEntry.enrollmentRequired'
  | 'promo.codeEntry.budgetSoftStop'
  | 'promo.codeEntry.budgetExhausted'
  | 'promo.codeEntry.loyaltyMutualExclusion'
  | 'promo.codeEntry.holdActiveRemoveForbidden'
  | 'promo.codeEntry.holdAlreadyActive'
  | 'promo.codeEntry.holdExpired'
  | 'promo.codeEntry.rateLimited'
  | 'promo.codeEntry.eventNotActive'
  | 'promo.codeEntry.zeroMagnitude'
  | 'promo.codeEntry.priceRevalidationRequired'
  | 'promo.codeEntry.networkError'
  | 'promo.codeEntry.empty'
  | 'promo.codeEntry.timeout'
  | 'promo.codeEntry.genericError';

/** §20 envelope `recovery` next-step copy (separate from short `error` / messageKey). */
export type PromoCodeApplyRecoveryMessageKey =
  | 'promo.codeEntry.invalidClassRecovery'
  | 'promo.codeEntry.moduleDisabledRecovery'
  | 'promo.codeEntry.tenantDisabledRecovery'
  | 'promo.codeEntry.shadowOrDisabledRecovery'
  | 'promo.codeEntry.rateLimitedRecovery';

export type PromoCodeApplyRecoveryAction =
  | 'none'
  | 'restart_checkout'
  | 'sign_in'
  | 'clear_stale_ui'
  | 'cancel_payment';

export interface PromoCodeApplyErrorMapping {
  readonly messageKey: PromoCodeApplyUserMessageKey;
  readonly recovery: PromoCodeApplyRecoveryAction;
  /** True when failure was a transport/network problem (offline gate). */
  readonly isNetworkFailure: boolean;
  /** Optional next-step recovery line from flat §20 envelopes. */
  readonly recoveryMessageKey?: PromoCodeApplyRecoveryMessageKey;
}

/** Compose short error + §20 recovery next-step for FormField `errorText`. */
export function formatPromoCodeApplyErrorText(
  mapping: PromoCodeApplyErrorMapping,
  t: (key: string) => string,
): string {
  const primary = t(mapping.messageKey);
  if (mapping.recoveryMessageKey == null) {
    return primary;
  }
  const recovery = t(mapping.recoveryMessageKey);
  if (recovery.length === 0 || recovery === mapping.recoveryMessageKey) {
    return primary;
  }
  return `${primary} ${recovery}`;
}

const OPAQUE_SET = new Set<string>(PROMO_CODE_OPAQUE_FAILURE_CODES);

export function mapPromoCodeApplyError(input: {
  readonly code?: string;
  readonly isNetworkFailure?: boolean;
  readonly isTimeout?: boolean;
  readonly isEmpty?: boolean;
}): PromoCodeApplyErrorMapping {
  if (input.isEmpty === true) {
    return { messageKey: 'promo.codeEntry.empty', recovery: 'none', isNetworkFailure: false };
  }
  if (input.isTimeout === true) {
    return { messageKey: 'promo.codeEntry.timeout', recovery: 'none', isNetworkFailure: false };
  }
  if (input.isNetworkFailure === true) {
    return {
      messageKey: 'promo.codeEntry.networkError',
      recovery: 'none',
      isNetworkFailure: true,
    };
  }

  const code = input.code;
  if (code != null && OPAQUE_SET.has(code)) {
    return {
      messageKey: 'promo.codeEntry.invalidClass',
      recoveryMessageKey: 'promo.codeEntry.invalidClassRecovery',
      recovery: 'none',
      isNetworkFailure: false,
    };
  }

  const knownCode: PromoCodeErrorCode | undefined =
    code != null && isPromoCodeErrorCode(code) ? code : undefined;

  switch (knownCode ?? code) {
    case 'PROMO_MODULE_DISABLED':
      return {
        messageKey: 'promo.codeEntry.moduleDisabled',
        recoveryMessageKey: 'promo.codeEntry.moduleDisabledRecovery',
        recovery: 'none',
        isNetworkFailure: false,
      };
    case 'PROMO_TENANT_DISABLED':
      return {
        messageKey: 'promo.codeEntry.tenantDisabled',
        recoveryMessageKey: 'promo.codeEntry.tenantDisabledRecovery',
        recovery: 'none',
        isNetworkFailure: false,
      };
    case 'PROMO_SHADOW_OR_DISABLED':
      return {
        messageKey: 'promo.codeEntry.shadowOrDisabled',
        recoveryMessageKey: 'promo.codeEntry.shadowOrDisabledRecovery',
        recovery: 'clear_stale_ui',
        isNetworkFailure: false,
      };
    case 'TENANT_ENTITLEMENT_READ_DENIED':
      return {
        messageKey: 'promo.codeEntry.entitlementReadDenied',
        recovery: 'none',
        isNetworkFailure: false,
      };
    case 'TENANT_ENTITLEMENT_WRITE_DENIED':
      return {
        messageKey: 'promo.codeEntry.entitlementWriteDenied',
        recovery: 'none',
        isNetworkFailure: false,
      };
    case 'TENANT_ENTITLEMENT_PROMO_SALES_POINT_NOT_ALLOWED':
      return {
        messageKey: 'promo.codeEntry.salesPointNotAllowed',
        recovery: 'none',
        isNetworkFailure: false,
      };
    case 'PROMO_CODE_CHECKOUT_SESSION_REQUIRED':
      return {
        messageKey: 'promo.codeEntry.checkoutSessionRequired',
        recovery: 'restart_checkout',
        isNetworkFailure: false,
      };
    case 'PROMO_CODE_GUEST_REQUIRED_SIGN_IN':
      return {
        messageKey: 'promo.codeEntry.guestSignInRequired',
        recovery: 'sign_in',
        isNetworkFailure: false,
      };
    case 'PROMO_CODE_CHANNEL_DENIED':
      return {
        messageKey: 'promo.codeEntry.channelDenied',
        recovery: 'none',
        isNetworkFailure: false,
      };
    case 'PROMO_CODE_MIN_SPEND':
      return {
        messageKey: 'promo.codeEntry.minSpend',
        recovery: 'none',
        isNetworkFailure: false,
      };
    case 'PROMO_CODE_ENROLLMENT_REQUIRED':
      return {
        messageKey: 'promo.codeEntry.enrollmentRequired',
        recovery: 'none',
        isNetworkFailure: false,
      };
    case 'PROMO_BUDGET_SOFT_STOP':
      return {
        messageKey: 'promo.codeEntry.budgetSoftStop',
        recovery: 'none',
        isNetworkFailure: false,
      };
    case 'PROMO_BUDGET_EXHAUSTED':
      return {
        messageKey: 'promo.codeEntry.budgetExhausted',
        recovery: 'none',
        isNetworkFailure: false,
      };
    case 'PROMO_LOYALTY_MUTUAL_EXCLUSION':
      return {
        messageKey: 'promo.codeEntry.loyaltyMutualExclusion',
        recovery: 'none',
        isNetworkFailure: false,
      };
    case 'PROMO_CODE_HOLD_ACTIVE_REMOVE_FORBIDDEN':
      return {
        messageKey: 'promo.codeEntry.holdActiveRemoveForbidden',
        recovery: 'cancel_payment',
        isNetworkFailure: false,
      };
    case 'PROMO_CODE_HOLD_ALREADY_ACTIVE':
      return {
        messageKey: 'promo.codeEntry.holdAlreadyActive',
        recovery: 'cancel_payment',
        isNetworkFailure: false,
      };
    case 'PROMO_CODE_HOLD_EXPIRED':
      return {
        messageKey: 'promo.codeEntry.holdExpired',
        recovery: 'none',
        isNetworkFailure: false,
      };
    case 'PROMO_CODE_RATE_LIMITED':
      return {
        messageKey: 'promo.codeEntry.rateLimited',
        recoveryMessageKey: 'promo.codeEntry.rateLimitedRecovery',
        recovery: 'none',
        isNetworkFailure: false,
      };
    case 'PROMO_CODE_EVENT_NOT_ACTIVE':
      return {
        messageKey: 'promo.codeEntry.eventNotActive',
        recovery: 'none',
        isNetworkFailure: false,
      };
    case 'PROMO_CODE_ZERO_MAGNITUDE':
      return {
        messageKey: 'promo.codeEntry.zeroMagnitude',
        recovery: 'none',
        isNetworkFailure: false,
      };
    case 'PROMO_PRICE_REVALIDATION_REQUIRED':
      return {
        messageKey: 'promo.codeEntry.priceRevalidationRequired',
        recovery: 'none',
        isNetworkFailure: false,
      };
    default:
      return {
        messageKey: 'promo.codeEntry.genericError',
        recovery: 'none',
        isNetworkFailure: false,
      };
  }
}

export function isPromoCodeNetworkError(error: unknown): boolean {
  if (error == null) {
    return false;
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true;
  }
  if (error instanceof TypeError) {
    return true;
  }
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    if (
      message.includes('failed to fetch') ||
      message.includes('networkerror') ||
      message.includes('network request failed') ||
      message.includes('load failed')
    ) {
      return true;
    }
    if (error.name === 'AbortError') {
      return false;
    }
  }
  return false;
}
